// Idempotent Pendo agent loader + identity dispatcher (GT1-208). Modelled on
// packages/neon-pay-web/src/loadNeonPayScript.ts: one script tag per page,
// marked with a data attribute so a remount can recognise its own injection.
//
// window.pendo is a per-page singleton, so all module-level state here is
// deliberately module-level — it must outlive the admin parcel's mount/unmount
// cycle, not reset with it.
import { logger } from '@/shell/observability';

import type { PendoAgent, PendoIdentity } from './types';

const SCRIPT_ATTR = 'data-pendo-agent';

function agentUrl(apiKey: string): string {
  return `https://cdn.pendo.io/agent/static/${apiKey}/pendo.js`;
}

let hasInitialized = false;
// True while a live agent has been silenced because the current org must not be
// recorded. Separate from `hasInitialized` because the agent keeps its identity
// through a suspension — it is muted, not torn down.
let isSuspended = false;
// Set when the agent script fails to load. A failed CDN fetch is not going to
// succeed on the next identity change, and a retry loop would queue commands
// forever — one attempt per page load is the whole policy.
let scriptFailed = false;

// The standard Pendo command-queue stub: callers can invoke the agent before the
// real script has downloaded, and the agent drains `_q` on load. `initialize`
// unshifts because it must run before any queued identify/track.
function createAgentStub(): PendoAgent {
  const queue: unknown[][] = [];
  return {
    _q: queue,
    initialize: (identity: PendoIdentity) => {
      queue.unshift(['initialize', identity]);
    },
    identify: (identity: PendoIdentity) => {
      queue.push(['identify', identity]);
    },
    updateOptions: (identity: Partial<PendoIdentity>) => {
      queue.push(['updateOptions', identity]);
    },
    pageLoad: () => {
      queue.push(['pageLoad']);
    },
    track: (name: string, props?: Record<string, unknown>) => {
      queue.push(props === undefined ? ['track', name] : ['track', name, props]);
    },
    // Queued like everything else so a suspension requested before the agent
    // script lands still takes effect: the agent drains `_q` in order, so the
    // stop lands immediately after the initialize it is meant to cancel.
    stopSendingEvents: () => {
      queue.push(['stopSendingEvents']);
    },
    startSendingEvents: () => {
      queue.push(['startSendingEvents']);
    },
    stopGuides: () => {
      queue.push(['stopGuides']);
    },
    startGuides: () => {
      queue.push(['startGuides']);
    },
  };
}

// Returns false when window.pendo already exists — i.e. some other script
// (a Single-SPA host shell) owns the agent. In that case we hand identities to
// the existing agent but never inject a second copy of the script.
function installAgentStub(): boolean {
  if (window.pendo) return false;
  window.pendo = createAgentStub();
  return true;
}

function injectAgentScript(apiKey: string): void {
  if (document.querySelector(`script[${SCRIPT_ATTR}]`)) return;
  const script = document.createElement('script');
  script.async = true;
  script.src = agentUrl(apiKey);
  script.setAttribute(SCRIPT_ATTR, '');
  script.addEventListener('error', () => {
    scriptFailed = true;
    logger.warn('pendo agent script failed to load', { src: script.src });
  });
  document.head.appendChild(script);
}

/**
 * Install the agent (once) and hand it the current admin identity.
 *
 * The first call initializes; every later call identifies. Re-initializing a
 * live agent logs a Pendo warning and is not supported, whereas identify() is
 * the sanctioned way to switch the full visitor + account context (which is what
 * a supervisor assuming a different org is). The `hasInitialized` flag is
 * module-level so it survives a parcel remount — the page-singleton agent must
 * never be initialized twice.
 */
export function applyPendoIdentity(apiKey: string, identity: PendoIdentity): void {
  if (!apiKey || scriptFailed) return;
  if (installAgentStub()) injectAgentScript(apiKey);
  const agent = window.pendo;
  if (!agent) return;
  if (isSuspended) {
    isSuspended = false;
    agent.startSendingEvents?.();
    agent.startGuides?.();
  }
  if (hasInitialized) {
    agent.identify(identity);
    return;
  }
  hasInitialized = true;
  agent.initialize(identity);
}

/**
 * Mute a live agent for an org that must not be recorded.
 *
 * The agent is a page singleton that cannot be un-initialized, so an admin who
 * moves from a recorded org into an excluded one (a supervisor assuming a
 * sandbox org) would otherwise keep generating events billed and attributed to
 * the org they left. Muting is the only way to stop that without
 * `clearSession()`, which starts a fresh anonymous visitor and costs MAU.
 *
 * Guides are stopped alongside events: an excluded org is excluded in both
 * directions, not just in what it reports back.
 *
 * A no-op before the first identity — nothing has been initialized to mute.
 */
export function suspendPendo(): void {
  if (!hasInitialized || isSuspended) return;
  const agent = window.pendo;
  if (!agent) return;
  // Both controls, or neither. `window.pendo` may belong to a host shell whose
  // object does not implement them, and recording a suspension that did not
  // happen is worse than not suspending: the flag latches, so every later
  // attempt short-circuits — including the one after the real agent replaces a
  // host's command-queue stub, which is exactly when muting would start working.
  // Surfaced rather than swallowed: an org that must not be recorded still being
  // recorded is not something to discover from a dashboard months later.
  if (!agent.stopSendingEvents || !agent.stopGuides) {
    logger.warn('pendo agent cannot be suspended: mute controls unavailable');
    return;
  }
  isSuspended = true;
  agent.stopSendingEvents();
  agent.stopGuides();
}

export function resetPendoClientForTests(): void {
  hasInitialized = false;
  isSuspended = false;
  scriptFailed = false;
  document.querySelector(`script[${SCRIPT_ATTR}]`)?.remove();
}
